// Treatment Wizard UI. Logic lives in rules.js; copy/config in config.js.
// Answers stay in memory only: nothing is stored or sent anywhere.
import { AREAS, QUESTIONS, DEFAULT_CARDS, DEFAULT_CONSULTATION_URL, DEFAULT_DISCLAIMER, INVALID_MESSAGE } from './config';
import {
    buildResult,
    calcBmi,
    dedupeAreas,
    formatBmi,
    needsAbdomenQuestion,
    needsPriority,
    validateMeasurements,
} from './rules';

const TOTAL_QUESTIONS = 8;
const QUESTION_NUMBER = {
    areas: 1,
    priority: 1,
    measure: 2,
    concern: 3,
    description: 4,
    abdomen: 4,
    downtime: 5,
    change: 6,
    budget: 7,
    weightChange: 8,
};
const SINGLE_CHOICE = ['concern', 'description', 'abdomen', 'downtime', 'change', 'budget', 'weightChange'];
const ANSWER_KEY = { abdomen: 'abdomenSkin' };

function h(tag, attrs = {}, children = []) {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
        if (v === null || v === undefined || v === false) return;
        if (k === 'class') el.className = v;
        else if (k === 'text') el.textContent = v;
        else el.setAttribute(k, v === true ? '' : v);
    });
    [].concat(children).forEach((c) => c && el.append(c));
    return el;
}

function emptyAnswers() {
    return {
        areas: [],
        priority: null,
        heightFeet: '',
        heightInches: '',
        weight: '',
        concern: null,
        description: null,
        abdomenSkin: null,
        downtime: null,
        change: null,
        budget: null,
        weightChange: null,
    };
}

function initWizard(root) {
    if (root.dataset.wizardReady) return;
    root.dataset.wizardReady = '1';

    const stage = root.querySelector('.wizard__stage');
    const consultationUrl = root.dataset.consultationUrl || DEFAULT_CONSULTATION_URL;
    const disclaimer = root.dataset.disclaimer || DEFAULT_DISCLAIMER;
    let answers = emptyAnswers();
    let current = 'areas';

    const steps = () =>
        [
            'areas',
            needsPriority(answers) && 'priority',
            'measure',
            'concern',
            'description',
            needsAbdomenQuestion(answers) && 'abdomen',
            'downtime',
            'change',
            'budget',
            'weightChange',
        ].filter(Boolean);

    function getCard(id) {
        const el = root.querySelector(`[data-card="${id}"]`);
        const fallback = DEFAULT_CARDS[id];
        return {
            heading: (el && el.dataset.heading) || fallback.heading,
            body: (el && el.dataset.body) || fallback.body,
        };
    }

    function option(type, name, id, label, checked) {
        const input = h('input', { type, name, value: id, class: 'wizard__input', checked });
        return h('label', { class: 'wizard__option' }, [input, h('span', { class: 'wizard__box', 'aria-hidden': 'true' }), h('span', { class: 'wizard__label', text: label })]);
    }

    function frame(stepId, title, hint, body) {
        const num = QUESTION_NUMBER[stepId];
        const legend = h('legend', { class: 'wizard__legend' }, [
            h('span', { class: 'eyebrow wizard__eyebrow', text: `Question ${num} of ${TOTAL_QUESTIONS}` }),
            h('span', { class: 'wizard__title h2', tabindex: '-1', text: title }),
            hint ? h('span', { class: 'wizard__hint', text: hint }) : null,
        ]);
        return h('fieldset', { class: 'wizard__step', 'data-step': stepId }, [legend, body]);
    }

    function render() {
        const list = steps();
        const idx = list.indexOf(current);
        const isLast = idx === list.length - 1;
        let body;
        let fieldset;

        if (current === 'areas') {
            const q = QUESTIONS.areas;
            body = h('div', { class: 'wizard__options' }, AREAS.map((a) => option('checkbox', 'areas', a.id, a.label, answers.areas.includes(a.id))));
            fieldset = frame(current, q.title, q.hint, body);
        } else if (current === 'priority') {
            const q = QUESTIONS.priority;
            const labels = Object.fromEntries(AREAS.map((a) => [a.id, a.label]));
            body = h('div', { class: 'wizard__options' }, dedupeAreas(answers.areas).map((a) => option('radio', 'priority', a, labels[a], answers.priority === a)));
            fieldset = frame(current, q.title, q.hint, body);
        } else if (current === 'measure') {
            const q = QUESTIONS.measure;
            const num = (name, label, attrs) =>
                h('label', { class: 'wizard__field' }, [
                    h('span', { text: label }),
                    h('input', Object.assign({ type: 'number', inputmode: 'numeric', name, value: answers[name], required: true }, attrs)),
                ]);
            const bmiVal = calcBmi(answers.heightFeet, answers.heightInches, answers.weight);
            const bmi = h('input', { type: 'text', id: `${root.id}-bmi`, class: 'wizard__bmi', readonly: true, tabindex: '-1', value: validateMeasurements(answers) ? formatBmi(bmiVal) : '' });
            body = h('div', { class: 'wizard__measure' }, [
                num('heightFeet', 'Height (feet)', { min: 1, max: 8, step: 1 }),
                num('heightInches', 'Additional inches', { min: 0, max: 11, step: 1 }),
                num('weight', 'Weight (pounds)', { min: 1, step: 'any' }),
                h('label', { class: 'wizard__field wizard__field--bmi' }, [h('span', { text: 'Your BMI' }), bmi]),
            ]);
            fieldset = frame(current, q.title, q.hint, body);
        } else {
            const q = QUESTIONS[current];
            const key = ANSWER_KEY[current] || current;
            body = h('div', { class: 'wizard__options wizard__options--single' }, q.options.map((o) => option('radio', current, o.id, o.label, answers[key] === o.id)));
            fieldset = frame(current, q.title, q.hint, body);
        }

        const error = h('p', { class: 'wizard__error', role: 'alert' });
        const next = h('button', { type: 'button', class: 'btn btn--filled btn--no-arrow wizard__btn wizard__next', text: isLast ? 'See my results' : 'Next' });
        const back = idx > 0 ? h('button', { type: 'button', class: 'btn btn--filled btn--no-arrow wizard__btn wizard__prev', text: 'Back' }) : null;
        const nav = h('div', { class: 'wizard__nav' }, [back, next]);
        const form = h('form', { class: 'wizard__form', novalidate: true }, [fieldset, error, nav]);

        stage.replaceChildren(form);
        stage.querySelector('.wizard__title').focus({ preventScroll: true });

        // events
        form.addEventListener('submit', (e) => e.preventDefault());
        form.addEventListener('change', (e) => {
            const t = e.target;
            error.textContent = '';
            if (t.name === 'areas') {
                answers.areas = [...form.querySelectorAll('input[name="areas"]:checked')].map((i) => i.value);
                if (!dedupeAreas(answers.areas).includes(answers.priority)) answers.priority = null;
            } else if (t.name === 'priority') answers.priority = t.value;
            else if (SINGLE_CHOICE.includes(t.name)) answers[ANSWER_KEY[t.name] || t.name] = t.value;
        });
        form.addEventListener('input', (e) => {
            const t = e.target;
            if (!['heightFeet', 'heightInches', 'weight'].includes(t.name)) return;
            error.textContent = '';
            answers[t.name] = t.value;
            const out = form.querySelector('.wizard__bmi');
            out.value = validateMeasurements(answers) ? formatBmi(calcBmi(answers.heightFeet, answers.heightInches, answers.weight)) : '';
        });
        if (back) back.addEventListener('click', () => go(list[idx - 1]));
        next.addEventListener('click', () => {
            const msg = validate(current);
            if (msg) {
                error.textContent = msg;
                return;
            }
            // Steps list can change after an answer (priority / abdomen follow-up), so recompute.
            const fresh = steps();
            const nextId = fresh[fresh.indexOf(current) + 1];
            if (nextId) go(nextId);
            else showResult();
        });
    }

    function validate(step) {
        if (step === 'areas') return answers.areas.length ? '' : 'Please select at least one area.';
        if (step === 'priority') return answers.priority ? '' : 'Please choose your top priority.';
        if (step === 'measure') return validateMeasurements(answers) ? '' : 'Please enter a valid height (inches 0–11) and weight.';
        const key = ANSWER_KEY[step] || step;
        return answers[key] ? '' : 'Please choose an option to continue.';
    }

    function go(step) {
        current = step;
        render();
    }

    function showResult() {
        const r = buildResult(answers);
        const children = [];

        if (r.invalid) {
            children.push(h('h2', { class: 'wizard__title h2', tabindex: '-1', text: 'Please check your answers' }), h('p', { text: INVALID_MESSAGE }));
            const back = h('button', { type: 'button', class: 'btn btn--filled btn--no-arrow wizard__btn', text: 'Return to questions' });
            back.addEventListener('click', () => go('areas'));
            children.push(back);
        } else {
            children.push(
                h('span', { class: 'eyebrow wizard__eyebrow', text: 'Your guidance' }),
                h('h2', { class: 'wizard__title h2', tabindex: '-1', text: r.headline }),
                h('p', { class: 'wizard__message', text: r.message })
            );

            if (r.treatments.length) {
                const ids = r.showCombined ? ['COMBINED', ...r.treatments] : r.treatments;
                children.push(
                    h(
                        'div',
                        { class: 'wizard__cards' },
                        ids.map((id) => {
                            const c = getCard(id);
                            return h('article', { class: 'wizard__card' }, [h('h3', { class: 'wizard__card-title h4', text: c.heading }), h('p', { text: c.body })]);
                        })
                    )
                );
            }

            if (r.notes.length) {
                children.push(h('ul', { class: 'wizard__notes' }, r.notes.map((n) => h('li', { text: n }))));
            }

            const bits = [`Areas: ${r.summary.areas.join(', ')}`, `BMI: ${r.summary.bmi}`, `Weight history: ${r.summary.weightChange}`];
            children.push(h('p', { class: 'wizard__summary', text: bits.join(' · ') }));

            children.push(
                h('div', { class: 'wizard__nav wizard__nav--result' }, [
                    h('a', { class: 'btn btn--filled btn--no-arrow wizard__btn wizard__cta', href: consultationUrl, target: '_blank', rel: 'noopener', text: r.button }),
                    (() => {
                        const again = h('button', { type: 'button', class: 'wizard__back', text: 'Start over' });
                        again.addEventListener('click', () => {
                            answers = emptyAnswers();
                            go('areas');
                        });
                        return again;
                    })(),
                ])
            );
        }

        children.push(h('p', { class: 'wizard__disclaimer', text: disclaimer }));
        stage.replaceChildren(h('div', { class: 'wizard__result', 'aria-live': 'polite' }, children));
        stage.querySelector('.wizard__title').focus({ preventScroll: true });
    }

    render();
    // First render shouldn't steal focus/scroll on page load.
    if (document.activeElement && root.contains(document.activeElement)) document.activeElement.blur();
}

function initAll() {
    document.querySelectorAll('[data-treatment-wizard]').forEach(initWizard);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();
